import { UserProfile } from "@blacknut/javascript-sdk/dist";
import { logD, logE } from "@blacknut/logging/dist";
import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom-v5-compat";
import { useAppNavigation } from "src/utils/Navigation/useAppNavigation";
import { setDeeplink } from "../../redux/actions/App";
import {
  dismissFamilyInfoLoadError,
  loadFamilyInfo,
  selectProfile,
} from "../../redux/actions/Profiles";
import { dismissAuthenticationError } from "../../redux/actions/User";
import { State } from "../../redux/reducers";
const TAG = "Profiles";

//FIXME distinguish between returnUrl&deeplinkUrl
//In case of returnUrl honor active streams
//In case of deeplink cancel active streams
export const useProfiles = (props?: { returnUrl?: string }) => {
  const location = useLocation();
  const pReturnUrl = location.state?.returnUrl;
  const { navigate } = useAppNavigation();
  const { familyProfiles, profile } = useSelector((state: State) => state.profilesState);
  const { authentication, familyToken, user, deeplink, subscription } = useSelector(
    (state: State) => state.globalState,
  );
  const { error: authenticationError, inProgress: authenticating } = authentication;
  const { mainEntitlement } = subscription;
  const dispatch = useDispatch();

  React.useEffect(() => {
    const init = async () => {
      if (!familyProfiles.profiles && familyToken) {
        try {
          await loadFamilyInfo()(dispatch);
        } catch (e) {
          logE(TAG, "Caught error loading profiles", e);
        }
      }
    };
    init();
  }, [dispatch, familyProfiles.profiles, familyToken]);

  const _onSelectProfile = React.useCallback(
    async (aProfile: UserProfile, redirectUrl?: string) => {
      const returnUrl = (redirectUrl ?? props?.returnUrl) || pReturnUrl;
      if (aProfile.isPinLocked) {
        navigate(`/profiles/${aProfile.id}/pin`, { state: { returnUrl } });
      } else if (profile && profile.id === aProfile.id) {
        logD(TAG, "Selecting same profile");
        if (returnUrl) {
          navigate(returnUrl);
          if (deeplink) {
            setDeeplink()(dispatch);
          }
        } else {
          navigate("/catalog");
        }
      } else {
        try {
          await selectProfile(aProfile, null, true)(dispatch);

          // User selected a profile
          if (returnUrl) {
            // https://blacknut.atlassian.net/browse/CA-5737
            // Cannot use replace otherwise if modal is opened and the closed, history is empty
            navigate(returnUrl);
            if (deeplink) {
              setDeeplink()(dispatch);
            }
          } else {
            navigate("/catalog", { replace: true });
          }
        } catch (e) {
          logE(TAG, "Caught error selecting profile", e);
          // FIXME not trapped by test cases
        }
      }
    },
    [deeplink, dispatch, navigate, pReturnUrl, profile, props?.returnUrl],
  );

  return {
    profiles: familyProfiles.profiles,
    profile,
    loading: familyProfiles.inProgress,
    loadingError: familyProfiles.error,
    dismissLoadingError: React.useCallback(() => {
      dismissFamilyInfoLoadError()(dispatch);
    }, [dispatch]),
    dismissAuthenticationError: React.useCallback(() => {
      dismissAuthenticationError()(dispatch);
    }, [dispatch]),
    authenticating,
    authenticationError,
    user,
    onSelectProfile: _onSelectProfile,
    mainEntitlement,
  };
};
